import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { FilterValues } from "../../../../data-model/filterValues";
import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { MyMedicalPaOrdersState } from "../../myPaOrders/hooks/useMyPaOrdersTableData";
import { makePostCallForNextPagesDataFetch } from "../apis/makePostCallForNextPagesDataFetch";
import { createFilterPayloadForAllOpenOrdersForPostgres } from "../utils/generateFilterPayloadForAllOpenOrders";
import { useFilterPayload } from "./useFilterPayload";

/**
 * Handles infinite-scroll pagination for All Open Orders. When the table's visible
 * end index reaches the last loaded hit and more pages are available, automatically
 * fetches the next page and appends the results.
 *
 * @param validFilters - Currently applied filter values
 * @param initialDataFetchingExecuted - Whether the initial table data load has finished
 * @param searchTerm - Free-text search string
 * @param allOpenOrdersState - Current orders state (used to determine page boundaries)
 * @param setAllOpenOrdersState - State setter for the orders loading/data/error state
 * @param setTableData - State setter for the transformed table row data
 * @returns endIndex and setEndIndex to track the table's visible boundary
 */
export const useNextPageDataFetch = (
  validFilters: FilterValues[],
  initialDataFetchingExecuted: boolean,
  searchTerm: string,
  allOpenOrdersState: MyMedicalPaOrdersState,
  setAllOpenOrdersState: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersTableData[]>
  >,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { setTableDataForContext, getTableDataFromContext } =
    useTablesContext();
  const tableAttributes = getTableDataFromContext(TableNames.ALL_OPEN_ORDERS);
  const sortByField = tableAttributes?.sortByField;
  const sortAscending = tableAttributes?.sortAscending;
  const [endIndex, setEndIndex] = useState(0);
  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
  );

  // Use refs to store latest values without triggering re-renders
  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setAllOpenOrdersStateRef = useRef(setAllOpenOrdersState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  // Keep refs updated
  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setAllOpenOrdersStateRef.current = setAllOpenOrdersState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setAllOpenOrdersState,
    setTableData,
    setTableDataForContext,
  ]);

  const shouldFetchMoreData = useMemo(() => {
    return (
      endIndex === (allOpenOrdersState.data?.hits?.length ?? 0) - 1 &&
      (allOpenOrdersState.data?.page ?? 0) <
        (allOpenOrdersState.data?.total_pages ?? 0) - 1
    );
  }, [
    endIndex,
    allOpenOrdersState.data?.hits?.length,
    allOpenOrdersState.data?.page,
    allOpenOrdersState.data?.total_pages,
  ]);

  const payloadFinal = useMemo(() => {
    if (filterPayload === null) {
      return createFilterPayloadForAllOpenOrdersForPostgres(
        validFilters,
        searchTerm,
        getOrgIdForFetchExternalWorklist(),
        sortByField,
        sortAscending,
      );
    }
    return filterPayload;
  }, [filterPayload, validFilters, searchTerm, sortByField, sortAscending]);

  const payloadFinalWithPage =
    useMemo<SearchForMedicalPaOrdersFromPostgresParams>(
      () => ({
        ...payloadFinal,
        page: (allOpenOrdersState.data?.page ?? 0) + 1,
      }),
      [payloadFinal, allOpenOrdersState.data?.page],
    );

  const currentPage = allOpenOrdersState.data?.page ?? 0;

  const fetchNextPage = useCallback(() => {
    makePostCallForNextPagesDataFetch(
      payloadFinalWithPage,
      authStatusOptions as AuthStatusOptionModel[],
      setAllOpenOrdersStateRef.current,
      setTableDataRef.current,
    );
    setTableDataForContextRef.current(TableNames.ALL_OPEN_ORDERS, {
      lastBatchFetched: currentPage + 1,
    });
  }, [payloadFinalWithPage, authStatusOptions, currentPage]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
